import { forwardRef } from 'react';

import Spacings from '@dynatrace/strato-design-tokens/spacings';

import { Action } from './Action.js';
import { Navigation } from './Navigation.js';
import { Prefix } from './Prefix.js';
import { Subtitle } from './Subtitle.js';
import { Suffix } from './Suffix.js';
import { Title } from './Title.js';
import { titleSubtitleWrapperCSS } from './TitleBar.sty.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../core/utils/can-receive-default-text-format.js';
import { Divider } from '../divider/Divider.js';
import { Flex } from '../flex/Flex.js';

const COMPONENT_NAME = 'TitleBar';

//#region Typings
/**
 * Accepted properties for TitleBar
 * @public
 */
export interface TitleBarProps
  extends WithChildren,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /**
   * Whether a divider is displayed after the TitleBar component.
   * @defaultValue true
   */
  showDivider?: boolean;
}

//#endregion

/**
 * The TitleBar component can be used for a consistent layout for section titles and provides
 * layout slots for interactive and navigational elements.
 */
const TitleBarRoot = /* @__PURE__ */ forwardRef<HTMLDivElement, TitleBarProps>(
  (props: TitleBarProps, forwardedRef) => {
    const {
      children,
      showDivider = true,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    }: TitleBarProps = props;
    const [slots] = useSlots(children, {
      title: Title,
      subtitle: Subtitle,
      navigation: Navigation,
      suffix: Suffix,
      prefix: Prefix,
      action: Action,
    });

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    const titleIsText =
      !!slots.title && _canReceiveDefaultTextFormat(slots.title.props.children);
    const subtitleIsText =
      !!slots.subtitle &&
      _canReceiveDefaultTextFormat(slots.subtitle?.props.children);
    const titleAndSubtitleIsText =
      (titleIsText && !slots.subtitle) || (titleIsText && subtitleIsText);

    return (
      <Flex
        ref={forwardedRef}
        {...restProps}
        {...behavioralTrackingProps}
        width="100%"
        flexDirection="column"
        gap={0}
        className={consumerClassName}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        data-dt-component={COMPONENT_NAME}
      >
        <Flex flexDirection="column" gap={8}>
          {slots.navigation && (
            <Flex width="100%" justifyContent="space-between" gap={8}>
              {slots.navigation}
              {slots.action}
            </Flex>
          )}
          <Flex width="100%" justifyContent="space-between" gap={8}>
            <Flex flexWrap="wrap" gap={8} width="100%" minWidth={0}>
              {slots.prefix}
              {(slots.title || slots.subtitle) && (
                <div
                  className={titleSubtitleWrapperCSS({
                    childrenAreText: titleAndSubtitleIsText,
                  })}
                >
                  {slots.title}
                  {slots.subtitle}
                </div>
              )}
              {slots.suffix}
            </Flex>
            {!slots.navigation && slots.action}
          </Flex>
        </Flex>
        {showDivider && (
          <Divider flexItem style={{ marginTop: Spacings.Size8 }} aria-hidden />
        )}
      </Flex>
    );
  },
);

(TitleBarRoot as typeof TitleBarRoot & { displayName: string }).displayName =
  'TitleBar';

/**
 * The TitleBar component can be used for a consistent layout for section titles and provides
 * layout slots for interactive and navigational elements.
 * @public
 */
export const TitleBar = Object.assign(TitleBarRoot, {
  Title,
  Subtitle,
  Suffix,
  Navigation,
  Prefix,
  Action,
});
